/**
 * 自由参数编辑器
 * 参数行 = 参数名 / 参数值 / 单位（可选），支持增删改；受控组件，编辑结果通过 onChange 回传。
 * 用于素材表单与「AI 识别 → 用户确认」两个场景。
 */
import { Plus, Trash2 } from 'lucide-react';
import type { DrawingParamItem } from '../types/api';
import styles from './ParamEditor.module.css';

interface Props {
  value: DrawingParamItem[];
  onChange?: (params: DrawingParamItem[]) => void;
  readOnly?: boolean;
  maxRows?: number;
}

export default function ParamEditor({ value, onChange, readOnly = false, maxRows = 100 }: Props) {
  const rows = value || [];

  const update = (idx: number, patch: Partial<DrawingParamItem>) => {
    if (!onChange) return;
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const remove = (idx: number) => {
    if (!onChange) return;
    onChange(rows.filter((_, i) => i !== idx));
  };

  const add = () => {
    if (!onChange || rows.length >= maxRows) return;
    onChange([...rows, { name: '', value: '', unit: '' }]);
  };

  if (readOnly) {
    if (rows.length === 0) return <div className={styles.empty}>无参数</div>;
    return (
      <div className={styles.editor}>
        <div className={styles.headerRow}>
          <span>参数名</span><span>参数值</span><span>单位</span><span />
        </div>
        {rows.map((r, i) => (
          <div key={i} className={styles.row}>
            <input className={styles.nameInput} value={r.name} disabled />
            <input value={r.value} disabled />
            <input value={r.unit || ''} disabled />
            <span />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.headerRow}>
        <span>参数名</span><span>参数值</span><span>单位（可选）</span><span />
      </div>
      {rows.length === 0 && <div className={styles.empty}>暂无参数，点击下方按钮添加，或使用「AI 识别」自动提取</div>}
      {rows.map((r, i) => (
        <div key={i} className={styles.row}>
          <input
            className={!r.name.trim() && r.value ? `${styles.nameInput} ${styles.nameMissing}` : styles.nameInput}
            value={r.name}
            placeholder={r.value && !r.name.trim() ? '请补充参数名' : '如：罐身直径'}
            onChange={e => update(i, { name: e.target.value })}
          />
          <input
            value={r.value}
            placeholder="如：99"
            onChange={e => update(i, { value: e.target.value })}
          />
          <input
            value={r.unit || ''}
            placeholder="如：mm"
            onChange={e => update(i, { unit: e.target.value })}
          />
          <button type="button" className={styles.deleteBtn} title="删除该参数" onClick={() => remove(i)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" className={styles.addRow} disabled={rows.length >= maxRows} onClick={add}>
        <Plus size={13} /> 添加参数（{rows.length}/{maxRows}）
      </button>
    </div>
  );
}
